import React from "react";

function Privacypolicy() {
  return (
    <div
      className="container "
      style={{ fontSize: "18px", textAlign: "justify" }}
    >
      <h2 className="mt-3">
        <strong>Privacy Policy</strong>
      </h2>
      <p>
        <strong>Effective date:</strong> 11 July 2026
      </p>
      <p>
        Raive Wellness (&quot;Raive&quot;, &quot;RAIVÉ&quot;, &quot;we&quot;,
        &quot;our&quot;, or &quot;us&quot;) operates the RAIVÉ customer mobile
        application, related websites (including{" "}
        <a href="https://raive.life">https://raive.life</a> and{" "}
        <a href="https://booking.raive.life">https://booking.raive.life</a>),
        and wellness coaching services primarily offered in Dubai and other GCC
        countries.
      </p>
      <p>
        This Privacy Policy explains what information we collect, how we use and
        share it, how we protect it, and the choices available to you. By using
        our app or website, you consent to this Privacy Policy.
      </p>
      <p>
        If you have additional questions or require more information about our
        Privacy Policy, contact us at{" "}
        <a href="mailto:support@raive.life">support@raive.life</a>.
      </p>

      <h3>
        <strong>1. Scope</strong>
      </h3>
      <p>
        This policy applies to information collected through the RAIVÉ mobile
        apps (Android and iOS), our websites, and related online services. It
        covers customers, website visitors, and users who contact support.
      </p>

      <h3>
        <strong>2. Information we collect</strong>
      </h3>
      <p>
        The personal information that you are asked to provide, and the reasons
        why you are asked to provide it, will be made clear to you at the point
        we ask you to provide your personal information.
      </p>
      <ul>
        <li>
          <strong>Account and contact details:</strong> name, email address,
          phone number, and login credentials
        </li>
        <li>
          <strong>Profile and wellness information you provide:</strong> age,
          gender, height, weight, goals, country, preferences, and program
          intake details
        </li>
        <li>
          <strong>Service activity:</strong> program enrollment, appointments,
          diet intake, water intake, workouts, billing, and in-app usage
        </li>
        <li>
          <strong>Device and technical data:</strong> device type, OS version,
          app version, IP address, log data, and crash/diagnostic information
          needed to operate and secure the service
        </li>
        <li>
          <strong>Communications:</strong> messages, attachments, and support
          requests you send us
        </li>
      </ul>
      <p>
        If you contact us directly, we may receive additional information about
        you such as your name, email address, phone number, the contents of the
        message and/or attachments you may send us, and any other information
        you may choose to provide.
      </p>
      <p>
        When you register for an Account, we may ask for your contact
        information, including items such as name, address, email address, and
        telephone number.
      </p>

      <h3>
        <strong>3. Health and Fitness Data (Health Connect and Apple Health)</strong>
      </h3>
      <p>
        If you choose to connect health permissions, RAIVÉ may read limited
        fitness data to support wellness tracking:
      </p>
      <ul>
        <li>
          <strong>Android (Health Connect):</strong> we request read access only
          to daily step count (<code>READ_STEPS</code>). We do not access Steps
          Cadence, do not write health data to Health Connect, and do not
          request background or historical Health Connect read permissions
          beyond what is needed to show today&apos;s step progress.
        </li>
        <li>
          <strong>iOS (Apple Health / HealthKit):</strong> we request read-only
          access to step count. We do not write to Apple Health.
        </li>
      </ul>
      <p>
        Step data is used only to display your daily steps and progress on the
        Home screen wellness dashboard for enrolled customers. We do not sell
        health or fitness data. We do not use Health Connect or HealthKit data
        for advertising, data brokerage, insurance eligibility, employment
        decisions, or credit decisions.
      </p>
      <p>
        You can deny or revoke health permissions at any time in your device
        settings (Health Connect on Android, or Settings → Health → Data Access
        on iOS). If permission is revoked, the Daily Steps feature stops reading
        new step data.
      </p>

      <h3>
        <strong>4. How we use your information</strong>
      </h3>
      <p>We use the information we collect in various ways, including to:</p>
      <ul>
        <li>Create and manage your account</li>
        <li>Provide, operate, and maintain our website and mobile apps</li>
        <li>
          Deliver personalized fitness, nutrition, and coaching services
        </li>
        <li>
          Show wellness progress, including optional daily step tracking
        </li>
        <li>Improve, personalize, and expand our services</li>
        <li>Understand and analyze how you use our website and app</li>
        <li>
          Develop new products, services, features, and functionality
        </li>
        <li>
          Communicate with you about appointments, programs, billing, account
          updates, customer service, and (where permitted) marketing
        </li>
        <li>Send you emails</li>
        <li>Find and prevent fraud</li>
        <li>
          Comply with legal obligations applicable in the UAE and other GCC
          markets where we operate
        </li>
      </ul>

      <h3>
        <strong>5. Legal bases and regional compliance</strong>
      </h3>
      <p>
        We process personal data as needed to provide our services, with your
        consent where required, to meet contractual obligations, and to comply
        with applicable law. For users in the United Arab Emirates, we aim to
        handle personal data in line with the UAE Personal Data Protection Law
        (Federal Decree-Law No. 45 of 2021) and related guidance. Where GDPR,
        CCPA, or other local laws apply to you, we honor the rights described
        below to the extent required by those laws.
      </p>

      <h3>
        <strong>6. Data sharing</strong>
      </h3>
      <p>
        We do not sell your personal data or health/fitness data. We may share
        limited information only when needed to:
      </p>
      <ul>
        <li>
          Deliver coaching and program services (for example, with assigned
          trainers or service staff)
        </li>
        <li>Process payments through payment partners</li>
        <li>
          Operate infrastructure with trusted service providers under
          appropriate safeguards
        </li>
        <li>Comply with law, regulation, or valid legal process</li>
        <li>
          Protect the rights, safety, and security of Raive Wellness, our users,
          or the public
        </li>
      </ul>

      <h3>
        <strong>7. Log Files</strong>
      </h3>
      <p>
        Raive follows a standard procedure of using log files. These files log
        visitors when they visit websites. All hosting companies do this as part
        of hosting services&apos; analytics. The information collected by log
        files may include internet protocol (IP) addresses, browser type,
        Internet Service Provider (ISP), date and time stamp, referring/exit
        pages, and possibly the number of clicks. These are not linked to any
        information that is personally identifiable. The purpose of the
        information is for analyzing trends, administering the site, tracking
        users&apos; movement on the website, and gathering demographic
        information.
      </p>
      <p>
        Our websites may also use cookies or similar technologies to operate the
        site and improve performance. You can control cookies through your
        browser settings.
      </p>

      <h3>
        <strong>8. Advertising Partners Privacy Policies</strong>
      </h3>
      <p>
        Third-party ad servers or ad networks may use technologies like cookies,
        JavaScript, or Web Beacons in advertisements and links that appear on
        Raive, which are sent directly to users&apos; browsers. They may
        automatically receive your IP address when this occurs. These
        technologies are used to measure the effectiveness of advertising
        campaigns and/or to personalize advertising content.
      </p>
      <p>
        Note that Raive has no access to or control over these cookies that are
        used by third-party advertisers.
      </p>

      <h3>
        <strong>9. Third Party Privacy Policies</strong>
      </h3>
      <p>
        Raive&apos;s Privacy Policy does not apply to other advertisers or
        websites. We advise you to consult the respective Privacy Policies of
        those third-party services for more detailed information, including
        practices and instructions about how to opt out of certain options.
      </p>
      <p>
        You can choose to disable cookies through your individual browser
        options. More detailed information about cookie management can be found
        on the browsers&apos; respective websites.
      </p>

      <h3>
        <strong>10. Data storage, retention, and deletion</strong>
      </h3>
      <p>
        We store account, program, and related service data on secure servers
        used to operate RAIVÉ. Health step data read from Health Connect or
        Apple Health is used to display progress in the app and may be synced to
        your RAIVÉ account solely for wellness tracking within the service.
      </p>
      <p>
        We retain personal data only as long as needed to provide the service,
        meet legal/accounting requirements, resolve disputes, and enforce
        agreements. When data is no longer required, we delete or anonymize it
        where reasonably possible.
      </p>
      <p>
        You may request access, correction, or deletion of your account data by
        contacting{" "}
        <a href="mailto:support@raive.life">support@raive.life</a> or using
        in-app account deletion / support options where available. We will
        respond within a reasonable period and within any timeframe required by
        applicable law (for example, up to one month where GDPR-style timelines
        apply).
      </p>

      <h3>
        <strong>11. Data security</strong>
      </h3>
      <p>
        We use reasonable administrative, technical, and organizational
        safeguards, including encryption in transit where supported, access
        controls, and secure development practices. No method of transmission or
        storage is completely secure.
      </p>

      <h3>
        <strong>
          CCPA Privacy Rights (Do Not Sell My Personal Information)
        </strong>
      </h3>
      <p>
        Under the CCPA, among other rights, California consumers have the right
        to:
      </p>
      <ul>
        <li>
          Request that a business that collects a consumer&apos;s personal data
          disclose the categories and specific pieces of personal data that a
          business has collected about consumers.
        </li>
        <li>
          Request that a business delete any personal data about the consumer
          that a business has collected.
        </li>
        <li>
          Request that a business that sells a consumer&apos;s personal data,
          not sell the consumer&apos;s personal data.
        </li>
      </ul>
      <p>
        If you make a request, we have one month to respond to you. We do not
        sell personal information. To exercise these rights, contact{" "}
        <a href="mailto:support@raive.life">support@raive.life</a>.
      </p>

      <h3>
        <strong>GDPR Data Protection Rights</strong>
      </h3>
      <p>
        Where GDPR applies, every user is entitled to the following:
      </p>
      <p>
        The right to access – You have the right to request copies of your
        personal data. We may charge a small fee for this service where
        permitted.
      </p>
      <p>
        The right to rectification – You have the right to request that we
        correct any information you believe is inaccurate. You also have the
        right to request that we complete the information you believe is
        incomplete.
      </p>
      <p>
        The right to erasure – You have the right to request that we erase your
        personal data, under certain conditions.
      </p>
      <p>
        The right to restrict processing – You have the right to request that we
        restrict the processing of your personal data, under certain conditions.
      </p>
      <p>
        The right to object to processing – You have the right to object to our
        processing of your personal data, under certain conditions.
      </p>
      <p>
        The right to data portability – You have the right to request that we
        transfer the data that we have collected to another organization, or
        directly to you, under certain conditions.
      </p>
      <p>
        If you make a request, we have one month to respond to you. Contact{" "}
        <a href="mailto:support@raive.life">support@raive.life</a>.
      </p>

      <h3>
        <strong>12. Children&apos;s Information</strong>
      </h3>
      <p>
        Another part of our priority is adding protection for children while
        using the internet. We encourage parents and guardians to observe,
        participate in, and/or monitor and guide their online activity.
      </p>
      <p>
        Raive does not knowingly collect any Personal Identifiable Information
        from children under the age of 13 (or the higher age required in your
        jurisdiction). If you think that your child provided this kind of
        information on our website or app, contact us immediately at{" "}
        <a href="mailto:support@raive.life">support@raive.life</a> and we will
        do our best to promptly remove such information from our records.
      </p>

      <h3>
        <strong>13. International users (Dubai and GCC)</strong>
      </h3>
      <p>
        Our services are designed for customers in Dubai and other GCC
        countries. If you access RAIVÉ from another country, your information
        may be processed in locations where we or our service providers operate.
        We take steps appropriate to the sensitivity of the data and applicable
        law.
      </p>

      <h3>
        <strong>14. Changes to This Privacy Policy</strong>
      </h3>
      <p>
        We may update our Privacy Policy from time to time. Thus, we advise you
        to review this page periodically for any changes. We will notify you of
        any changes by posting the new Privacy Policy on this page. These
        changes are effective immediately after they are posted on this page
        and/or updated in the app.
      </p>

      <h3>
        <strong>15. Contact Us</strong>
      </h3>
      <p>
        <strong>Company:</strong> Raive Wellness
        <br />
        <strong>Email:</strong>{" "}
        <a href="mailto:support@raive.life">support@raive.life</a>
        <br />
        <strong>Website:</strong>{" "}
        <a href="https://raive.life">https://raive.life</a>
        <br />
        <strong>Privacy Policy URL:</strong>{" "}
        <a href="https://booking.raive.life/privacy-policy">
          https://booking.raive.life/privacy-policy
        </a>
      </p>
      <p>
        If you have any questions or suggestions about our Privacy Policy, do
        not hesitate to contact us at{" "}
        <a href="mailto:support@raive.life">support@raive.life</a>.
      </p>
    </div>
  );
}

export default Privacypolicy;
